import React, { useState, useEffect } from 'react';
import { 
  Download, 
  Upload, 
  Bell, 
  Shield, 
  Palette,
  Database
} from 'lucide-react';
import Layout from '../components/Layout';
import { useApp } from '../contexts/AppContext';
import toast from 'react-hot-toast';
import { 
  studentService, 
  classService, 
  attendanceService, 
  filterService,
  behaviorService,
  teacherAttendanceService
} from '../services/apiService';

const SettingsPage: React.FC = () => {
  const { } = useApp();
  const [activeTab, setActiveTab] = useState('export');

  // Settings state
  const [settings, setSettings] = useState({
    notifications: {
      emailDailySummary: true,
      emailWeeklyReports: true,
      emailBehaviorAlerts: false,
      emailLowAttendance: false,
      pushEnabled: true,
      pushAttendanceReminders: false,
      pushReportComplete: false
    },
    appearance: {
      theme: 'light',
      dateFormat: 'MM/DD/YYYY',
      timeFormat: '12'
    },
    privacy: {
      allowAnalytics: true,
      shareUsageStats: false,
      autoBackup: true,
      sessionTimeout: '60',
      requirePassword: false
    }
  });

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const tabs = [
    { id: 'export', name: 'Export/Import', icon: Download },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'appearance', name: 'Appearance', icon: Palette },
    { id: 'privacy', name: 'Privacy', icon: Shield },
    { id: 'database', name: 'Database', icon: Database }
  ];

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const savedSettings = localStorage.getItem('teacher-attendance-settings');
      if (savedSettings) {
        setSettings(JSON.parse(savedSettings));
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const saveSettings = async () => {
    try {
      localStorage.setItem('teacher-attendance-settings', JSON.stringify(settings));
      setHasUnsavedChanges(false);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const handleSettingChange = (category: string, key: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category as keyof typeof prev],
        [key]: value
      }
    }));
    setHasUnsavedChanges(true);
  };

  const exportData = async () => {
    try {
      const [students, classes, attendance, behavior, teacherAttendance, filters] = await Promise.all([
        studentService.getAll(),
        classService.getAll(),
        attendanceService.getAll(),
        behaviorService.getAll(),
        teacherAttendanceService.getAll(),
        filterService.getAll()
      ]);

      const exportData = {
        students,
        classes,
        attendance,
        behavior,
        teacherAttendance,
        filters,
        exportDate: new Date().toISOString(),
        version: '1.0.0'
      };

      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `teacher-attendance-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Data exported successfully');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const importData = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = JSON.parse(text);

      // Import students
      if (data.students && Array.isArray(data.students)) {
        for (const student of data.students) {
          try {
            await studentService.create({
              firstName: student.firstName,
              lastName: student.lastName,
              email: student.email || '',
              phone: student.phone || '',
              dateOfBirth: student.dateOfBirth || '',
              parentName: student.parentName || '',
              parentEmail: student.parentEmail || '',
              notes: student.notes || ''
            });
          } catch (error) {
            console.error('Failed to import student:', student, error);
          }
        }
      }

      // Import classes
      if (data.classes && Array.isArray(data.classes)) {
        for (const classItem of data.classes) {
          try {
            await classService.create({
              name: classItem.name,
              description: classItem.description || '',
              subject: classItem.subject || '',
              schedule: classItem.schedule || []
            });
          } catch (error) {
            console.error('Failed to import class:', classItem, error);
          }
        }
      }

      // Import attendance records
      if (data.attendance && Array.isArray(data.attendance)) {
        for (const record of data.attendance) {
          try {
            await attendanceService.create({
              studentId: record.studentId,
              classId: record.classId,
              date: record.date,
              status: record.status,
              notes: record.notes || ''
            });
          } catch (error) {
            console.error('Failed to import attendance record:', record, error);
          }
        }
      }

      // Import behavior incidents
      if (data.behavior && Array.isArray(data.behavior)) {
        for (const incident of data.behavior) {
          try {
            await behaviorService.create({
              studentId: incident.studentId,
              classId: incident.classId,
              type: incident.type,
              description: incident.description,
              severity: incident.severity || 'medium',
              resolution: incident.resolution || '',
              timestamp: new Date(incident.timestamp || new Date()),
              resolved: incident.resolved || false
            });
          } catch (error) {
            console.error('Failed to import behavior incident:', incident, error);
          }
        }
      }

      // Import teacher attendance
      if (data.teacherAttendance && Array.isArray(data.teacherAttendance)) {
        for (const record of data.teacherAttendance) {
          try {
            await teacherAttendanceService.create({
              classId: record.classId,
              date: record.date,
              status: record.status,
              notes: record.notes || ''
            });
          } catch (error) {
            console.error('Failed to import teacher attendance:', record, error);
          }
        }
      }

      // Import filters
      if (data.filters && Array.isArray(data.filters)) {
        for (const filter of data.filters) {
          try {
            await filterService.create({
              name: filter.name,
              type: filter.type,
              filters: filter.filters || {}
            });
          } catch (error) {
            console.error('Failed to import filter:', filter, error);
          }
        }
      }

      toast.success('Data imported successfully');
      window.location.reload(); // Refresh to show imported data
    } catch (error) {
      toast.error('Failed to import data. Please check the file format.');
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'export':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Export Data</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Export all your data to a JSON file for backup purposes.
              </p>
              <button
                onClick={exportData}
                className="btn-primary"
              >
                <Download className="h-4 w-4 mr-2" />
                Export All Data
              </button>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Import Data</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Import data from a previously exported JSON file.
              </p>
              <input
                type="file"
                accept=".json"
                onChange={importData}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900 dark:file:text-blue-300"
              />
            </div>
          </div>
        );

      case 'notifications':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Notification Settings</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email Daily Summary
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Receive daily attendance summaries via email
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.notifications.emailDailySummary}
                  onChange={(e) => handleSettingChange('notifications', 'emailDailySummary', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email Weekly Reports
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Receive weekly attendance reports via email
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.notifications.emailWeeklyReports}
                  onChange={(e) => handleSettingChange('notifications', 'emailWeeklyReports', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email Behavior Alerts
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Get notified when behavior incidents occur
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.notifications.emailBehaviorAlerts}
                  onChange={(e) => handleSettingChange('notifications', 'emailBehaviorAlerts', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </div>
            </div>
          </div>
        );

      case 'appearance':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Appearance Settings</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Theme
                </label>
                <select
                  value={settings.appearance.theme}
                  onChange={(e) => handleSettingChange('appearance', 'theme', e.target.value)}
                  className="input-field"
                >
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="auto">Auto</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Date Format
                </label>
                <select
                  value={settings.appearance.dateFormat}
                  onChange={(e) => handleSettingChange('appearance', 'dateFormat', e.target.value)}
                  className="input-field"
                >
                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Time Format
                </label>
                <select
                  value={settings.appearance.timeFormat}
                  onChange={(e) => handleSettingChange('appearance', 'timeFormat', e.target.value)}
                  className="input-field"
                >
                  <option value="12">12 Hour</option>
                  <option value="24">24 Hour</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 'privacy':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Privacy Settings</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Allow Analytics
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Help improve the app by sharing anonymous usage data
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.privacy.allowAnalytics}
                  onChange={(e) => handleSettingChange('privacy', 'allowAnalytics', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Auto Backup
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Automatically backup your data to the cloud
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.privacy.autoBackup}
                  onChange={(e) => handleSettingChange('privacy', 'autoBackup', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </div>
            </div>
          </div>
        );

      case 'database':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Database Information</h3>
            
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Your data is stored securely in the database. All information is encrypted and backed up regularly.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Database Status</h4>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-600 dark:text-gray-400">Connected</span>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Last Backup</h4>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
                </span>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Settings</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Manage your application preferences and data
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <div className="lg:w-64">
            <nav className="space-y-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === tab.id
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800'
                  }`}
                >
                  <tab.icon className="h-4 w-4 mr-3" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          {/* Main content */}
          <div className="flex-1">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="p-6">
                {renderTabContent()}
              </div>
              
              {hasUnsavedChanges && (
                <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      You have unsaved changes
                    </p>
                    <button
                      onClick={saveSettings}
                      className="btn-primary"
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SettingsPage;